/**
 * ============================================================================
 * SUBMISSION HOOK - useSubmission  
 * ============================================================================
 * Centralized submission workflow management
 * Version: v3.10.0-CLEAR-MAP
 * 
 * This hook manages the ENTIRE submission flow:
 * - Image capture & selection
 * - Location & GPS capture
 * - Business search (Google Places)
 * - Quick Shot workflow
 * - Regular inspection workflow
 * - AI analysis & processing
 * - Success/confirmation popups
 * - Attachments & notes
 */

import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useUI } from './useUI';
import { useInspections } from './useInspections';
import usePermissions from './usePermissions';
import * as api from '../services/apiService';

// This is a hook-only module (no Context needed unless we want global submission state)
// For now, we'll use it as a custom hook that can be called in App

export const useSubmission = () => {
  const { submittedOnBehalfOf, currentUser } = useAuth();
  const { showToast } = useUI();
  const { refreshInspections, setPendingOpenInspectionId, loadInspections, loadDueInspections } = useInspections();
  const { getCurrentLocation } = usePermissions();
  
  // Prefer a real email if we have one; keep display name separate.
  const submittedByEmail =
    submittedOnBehalfOf && typeof submittedOnBehalfOf === 'string' && submittedOnBehalfOf.includes('@')
      ? submittedOnBehalfOf.trim().toLowerCase()
      : '';
  
  // ============================================================================
  // IMAGE STATE
  // ============================================================================
  const [selectedImage, setSelectedImage] = useState(null);
  const [capturedImages, setCapturedImages] = useState([]);
  
  // ============================================================================
  // LOCATION & GPS STATE
  // ============================================================================
  const [location, setLocation] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [gpsData, setGpsData] = useState(null);
  const [isCapturingGPS, setIsCapturingGPS] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [nearestOffice, setNearestOffice] = useState(null);
  const [loadingNearest, setLoadingNearest] = useState(false);
  
  // ============================================================================
  // BUSINESS SEARCH STATE (Google Places)
  // ============================================================================
  const [businessSuggestions, setBusinessSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [locationSelectedFromPopup, setLocationSelectedFromPopup] = useState(false);
  
  // ============================================================================
  // SUBMISSION FLOW STATE
  // ============================================================================
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inspectionResult, setInspectionResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  
  // ============================================================================
  // CONFIRMATION & SUCCESS POPUPS
  // ============================================================================
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [confirmationData, setConfirmationData] = useState({ image: null, address: '' });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupData, setSuccessPopupData] = useState(null);
  
  // ============================================================================
  // ATTACHMENTS & NOTES STATE
  // ============================================================================
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [quickShotOtherNotes, setQuickShotOtherNotes] = useState('');
  const [mainPageAttachments, setMainPageAttachments] = useState([]);
  
  // ============================================================================
  // GAMIFICATION STATE (Submission-related)
  // ============================================================================
  const [consecutiveScans, setConsecutiveScans] = useState(0);
  
  // ============================================================================
  // GPS CAPTURE FUNCTION - SMART PERMISSION CHECK
  // ============================================================================
  const captureGPS = async () => {
    setIsCapturingGPS(true);
    setGpsError(null);
    
    if (!navigator.geolocation) {
      setGpsError('GPS not supported by this browser');
      setIsCapturingGPS(false);
      return null;
    }
    
    try {
      // SMART PERMISSION CHECK: Uses hook that checks permission status first
      console.log('📍 Requesting location (smart check - won\'t ask if already granted)...');
      const position = await getCurrentLocation();
      
      if (!position) {
        // Permission denied or error
        setGpsError('Location permission denied');
        setIsCapturingGPS(false);
        return null;
      }
      
      const gps = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date().toISOString()
      };
      
      setGpsData(gps);
      console.log('📍 ✅ GPS captured');
      return gps; // Return GPS data for onCapture to use
    } catch (error) {
      console.error('GPS capture failed:', error);
      setGpsError(error.message || 'Failed to capture GPS');
      return null;
    } finally {
      setIsCapturingGPS(false);
    }
  };
  
  // ============================================================================
  // BUSINESS SEARCH FUNCTION
  // ============================================================================
  const fetchBusinessSuggestions = async (latitude, longitude, showToast) => {
    setIsLoadingSuggestions(true);
    console.log(`🔍 Fetching nearby businesses for: ${latitude}, ${longitude}`);
    
    try {
      // 🎯 ENHANCED: Progressive radius expansion to get at least 5 results
      // Optimized for accuracy: Start at 25m (user is likely IN this building)
      // Keep expanding until we have at least 5 businesses OR reach max radius
      const radii = [25, 75, 150, 500, 1000, 2000, 5000];
      const MIN_RESULTS = 5; // Minimum businesses to show user good options
      let finalData = null;
      let usedRadius = 0;
      
      for (const radius of radii) {
        // eslint-disable-next-line no-await-in-loop
        const data = await api.fetchBusinessSuggestions(latitude, longitude, radius);
        const count = data?.places?.length || 0;
        console.log(`📍 Places radius=${radius}m → ${count} businesses`);
        finalData = data;
        usedRadius = radius;
        // 🔧 FIX: Keep expanding until we have at least MIN_RESULTS
        if (count >= MIN_RESULTS) {
          console.log(`✅ Got ${count} businesses at ${radius}m - sufficient`);
          break;
        }
        // If we haven't reached MIN_RESULTS but have some, keep going
        if (count > 0 && count < MIN_RESULTS) {
          console.log(`⚠️ Only ${count} businesses at ${radius}m - expanding to find more`);
        }
      }

      const places = finalData?.places || [];
      
      // Log first result distance for quality monitoring
      if (places.length > 0 && places[0].distance_meters !== undefined) {
        console.log(`✅ Found ${places.length} nearby businesses (first result: ${places[0].distance_meters}m away)`);
      } else {
        console.log(`✅ Found ${places.length} nearby businesses`);
      }

      setBusinessSuggestions(places);
      setShowSuggestions(true);

      const canToast = typeof showToast === 'function';

      if (places.length === 0) {
        console.warn('⚠️ No businesses found in response (after radius fallback up to 2km)');
        if (canToast) {
          showToast('📍 No nearby businesses found. You can enter manually.', 'info', 4000);
        }
      } else if (canToast) {
        // Show distance in toast if available
        const firstDistance = places[0]?.distance_meters;
        if (firstDistance !== undefined && firstDistance < 50) {
          showToast(`🎯 Found ${places.length} nearby (closest: ${firstDistance}m)`, 'success', 3000);
        } else {
          showToast(`📍 Found ${places.length} nearby businesses!`, 'success', 3000);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching business suggestions:', error);
      setBusinessSuggestions([]);
      if (typeof showToast === 'function') {
        showToast('📍 GPS search unavailable. Please enter business name manually.', 'warning', 4000);
      }
    } finally {
      setIsLoadingSuggestions(false);
    }
  };
  
  // ============================================================================
  // FILE UPLOAD HANDLERS
  // ============================================================================
  const handleFileUpload = (event, playSound, showToast) => {
    const file = event.target.files[0];
    console.log('File upload triggered, file:', file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('File loaded, setting image in background');
        // Set image but DON'T show preview on capture page
        setSelectedImage(e.target.result);
        
        // Play camera sound for feedback
        if (playSound) playSound('camera-shutter', 'file-upload-success');
        
        // Auto-capture GPS when file is uploaded
        if (!gpsData && !isCapturingGPS) {
          console.log('Triggering GPS capture from file upload');
          captureGPS();
        }
        
        // IMMEDIATE: Show location popup after image loads
        setTimeout(() => {
          console.log('✅ Image loaded, triggering location popup');
          setShowLocationPopup(true);
          if (showToast) showToast('📍 Select a location for this image', 'success');
        }, 500); // Small delay to ensure GPS capture starts
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMainPageFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    console.log('Main page file upload triggered, files:', files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = {
          name: file.name,
          data: e.target.result.split(',')[1], // Remove data:type;base64, prefix
          type: file.type,
          timestamp: new Date().toISOString()
        };
        setMainPageAttachments(prev => [...prev, fileData]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  // ============================================================================
  // SUBMISSION WORKFLOWS
  // ============================================================================
  
  /**
   * Handle Quick Shot Submit
   * @param {Function} playSound - Sound effect function
   * @param {Function} showToast - Toast notification function
   * @param {Function} setSubmissionBanner - Banner state setter
   * @param {string} submittedBy - User name
   * @param {string|null} imageOverride - Optional image data (overrides selectedImage)
   * @param {Object|null} successCallbacks - Optional callbacks for success popup
   * @param {string|null} businessNameOverride - 🔧 FIX: Direct business name to avoid stale closure
   * @param {string|null} notesOverride - 🔧 FIX: Direct notes from SelectAndSubmitModal
   */
  const handleQuickShotSubmit = async (
    playSound,
    showToast,
    setSubmissionBanner,
    submittedBy,
    imageOverride = null,
    successCallbacks = null,
    businessNameOverride = null,
    notesOverride = null,
    businessAddressOverride = null,
    businessPhoneOverride = null,
    locationSourceOverride = null,
    gpsDataOverride = null,
    serialNumberOverride = null  // NEW: For user-corrected serial numbers
  ) => {
    if (isSubmitting) {
      console.log('⚠️ Already submitting, ignoring duplicate click');
      return;
    }

    setIsSubmitting(true);
    
    // 🔧 FIX: Use overrides if provided to avoid stale closure
    const finalBusinessName = businessNameOverride || businessName;
    const finalBusinessAddress = businessAddressOverride || '';
    const finalBusinessPhone = businessPhoneOverride || '';
    const finalLocationSource = locationSourceOverride || '';
    const finalNotes = notesOverride || quickShotOtherNotes;
    const finalSerialNumber = serialNumberOverride || null;  // User-corrected serial
    
    console.log('📸 QUICK SHOT SUBMIT: Starting submission process...');
    
    // 🔥 DISPATCH EVENT: Submission started (for leaderboard)
    window.dispatchEvent(new CustomEvent('submission-started', {
      detail: { 
        image: imageOverride || selectedImage, 
        businessName: finalBusinessName.trim(),
        submittedBy 
      }
    }));
    
    try {
      const submissionTraceId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const sessionToken = localStorage.getItem('session_token');
      const imageToUse = imageOverride || selectedImage;
      if (!imageToUse) {
        throw new Error('No image available for submission');
      }
      const imageBase64 = imageToUse.split(',')[1]; // Remove data URL prefix (full-res 1200×2000 PORTRAIT)
      
      // 🆕 Extract thumbnail if available (300×500)
      const capturedImage = capturedImages && capturedImages.length > 0 ? capturedImages[0] : null;
      const thumbnailBase64 = capturedImage?.thumbnailData ? capturedImage.thumbnailData.split(',')[1] : null;
      
      console.log('Submitting to backend with session token:', sessionToken);
      
    const effectiveGpsData = gpsDataOverride || gpsData;
    // Prepare location string from business name and GPS
    const locationString = finalBusinessName.trim() + (effectiveGpsData ? ` (GPS: ${effectiveGpsData.latitude}, ${effectiveGpsData.longitude})` : '');
      
      // Submit to backend for analysis (same as original analyzeImage function) - now runs in background
      const response = await api.createInspection({
        image_base64: imageBase64, // 🔧 Full-res 1200×2000 PORTRAIT for AI processing and admin viewing
        thumbnail_base64: thumbnailBase64, // 🆕 Thumbnail 300×500 for lists/previews
        location: locationString,
        notes: finalNotes.trim(), // ✅ Primary notes field (used by admin UI)
        business_name: finalBusinessName.trim(),
        business_address: finalBusinessAddress,
        business_phone: finalBusinessPhone,
        location_source: finalLocationSource,
        submission_trace_id: submissionTraceId,
        submitted_by: submittedBy.trim() || "Anonymous",
        submitted_by_email: submittedByEmail || undefined,
        gps_data: effectiveGpsData,
        mode: "quick_shot",
        serial_number_override: finalSerialNumber,  // 📝 User-corrected serial number
        user_corrected: finalSerialNumber ? true : false,  // Flag that user provided data
        // Main page attachments and voice data
        main_page_attachments: mainPageAttachments.map(file => ({
          name: file.name,
          data: file.data,
          type: file.type,
          timestamp: file.timestamp
        })),
        // ✅ FIX: Don't duplicate notes in main_page_voice_notes (removed)
        main_page_voice_audio: mainPageAttachments.filter(file => file.type.startsWith('audio/')).map(file => ({
          name: file.name,
          data: file.data,
          type: file.type,
          timestamp: file.timestamp
        }))
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Backend analysis complete');
        setInspectionResult(result);
        
        // 🔍 Check tag validation status and AI confidence
        const isValidTag = result.is_valid_tag !== false; // Default to true if not set
        const validationReason = result.validation_reason || result.inspection?.validation_reason || '';
        const confidenceScore = result.confidence_score || result.inspection?.confidence_score || 1.0;
        const pointsAwarded = result.points_awarded || (isValidTag ? 1 : 0);
        
        console.log(`🤖 AI Confidence: ${Math.round(confidenceScore * 100)}%`);
        console.log(`🏷️ Valid Tag: ${isValidTag} | Reason: ${validationReason || 'N/A'}`);
        
        // 🚫 INVALID TAG = Show Rejection Modal (not success!)
        if (!isValidTag) {
          console.log('❌ Invalid tag detected - showing rejection modal');
          
          const rejectionData = {
            inspection: result,
            location: businessName,
            points: 0,
            isProcessing: false,
            isRejection: true,  // 🚫 Key flag for SuccessModal to show rejection state
            is_valid_tag: false,
            validation_reason: validationReason,
            rejection_reason: validationReason
          };
          
          // Show rejection state in SuccessModal
          if (successCallbacks) {
            successCallbacks.setSuccessPopupData(rejectionData);
            successCallbacks.setShowSuccessPopup(true);
          } else {
            setSuccessPopupData(rejectionData);
            setShowSuccessPopup(true);
          }
          
          // Don't track as consecutive scan (rejection breaks streak)
          setConsecutiveScans(0);
          
          // Reset form
          setSelectedImage(null);
          setCapturedImages([]);
          setQuickShotOtherNotes('');
          setMainPageAttachments([]);
          
          return; // Stop here - rejection shown
        }
        
        // 📝 LOW CONFIDENCE = Need More Info Modal
        if (confidenceScore < 0.8 && window.showInfoRequestModal) {
          console.log('⚠️ Low confidence detected - showing info request modal');
          
          // 🔧 FIX: Pass inspection_id so user can edit the submission
          const inspectionId = result.inspection_id || result.inspection?.id || result.inspection?.inspection_id;
          console.log(`📝 Inspection created with ID: ${inspectionId}`);
          
          // Trigger info request modal via global callback
          window.showInfoRequestModal({
            confidence_score: confidenceScore,
            serial_number: result.inspection?.serial_number || '',
            business_name: businessName || '',
            address: result.inspection?.address || '',
            validation_reason: validationReason,
            image: selectedImage,
            // 🆕 Pass inspection data for Edit button
            inspection_id: inspectionId,
            inspection: result.inspection || result  // Full inspection object
          });
          
          // Hide success popup since we're showing info request instead
          if (successCallbacks) {
            successCallbacks.setShowSuccessPopup(false);
          }
          
          return; // Stop here, wait for user to correct data
        }
        
        // ✅ HIGH CONFIDENCE + VALID TAG = Success!
        // Track consecutive scans for encouraging messages
        setConsecutiveScans(prev => prev + 1);
        
        // Show success popup with data
        const successData = {
          inspection: result,
          location: businessName,
          points: pointsAwarded,
          isProcessing: false,  // Complete state
          isRejection: false,   // Explicitly not a rejection
          is_valid_tag: true
        };
        
        // Update success popup
        if (successCallbacks) {
          successCallbacks.setSuccessPopupData(successData);
        } else {
          setSuccessPopupData(successData);
        }
        
        // Reset form but keep location for convenience
        setSelectedImage(null);
        setCapturedImages([]);
        setQuickShotOtherNotes('');
        setMainPageAttachments([]);
        // Keep businessName and gpsData for next submission
        
        // Refresh inspections in background
        await refreshInspections();
        
        console.log('✅ Quick Shot submission complete!');
        
        // 🔥 DISPATCH EVENT: Submission completed (for leaderboard emoji burst)
        // 🎉 Store celebration flag for leaderboard (in case user navigates there later)
        sessionStorage.setItem('pendingCelebration', JSON.stringify({
          timestamp: Date.now(),
          points: pointsAwarded,
          businessName: result?.business_name || finalBusinessName
        }));
        
        window.dispatchEvent(new CustomEvent('submission-completed', {
          detail: { inspection: result, points: pointsAwarded }
        }));
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Quick Shot submission failed:', error);
      if (showToast) showToast('❌ Submission failed. Please try again.', 'error');
      
      // 🔥 DISPATCH EVENT: Submission failed
      window.dispatchEvent(new CustomEvent('submission-failed', {
        detail: { error: error.message }
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Analyze Image (Regular Mode)
   */
  const analyzeImage = async (playSound, showToast, setCurrentPage) => {
    setIsAnalyzing(true);
    setShowProcessingModal(true);
    setAnalyzeProgress(0);
    setInspectionResult(null);

    try {
      const requestStartedAtMs = Date.now();
      const imageBase64 = selectedImage.split(',')[1]; // Remove data URL prefix
      
      const response = await api.createInspection({
        image_base64: imageBase64,
        location: location,
        notes: notes,
        submitted_by: submittedOnBehalfOf.trim() || currentUser.trim() || 'Anonymous',
        submitted_by_email:
          (submittedOnBehalfOf && typeof submittedOnBehalfOf === 'string' && submittedOnBehalfOf.includes('@'))
            ? submittedOnBehalfOf.trim().toLowerCase()
            : undefined,
        gps_data: gpsData
      });

      if (response.ok) {
        const result = await response.json();
        // Treat success flag if present, but still handle without it
        if (result && result.success) {
          console.log(`Analysis succeeded in ${result.duration_ms} ms using ${result.model}`);
        }
        setInspectionResult(result);
        // Navigate to Validate page and open the freshly created record
        if (result && result.inspection_id) {
          setPendingOpenInspectionId(result.inspection_id);
        }
        setSelectedImage(null);
        setLocation('');
        setNotes('');
        // Keep submittedBy for future submissions - don't reset
        setGpsData(null);
        setGpsError(null);
        await refreshInspections();
        if (result && result.inspection_id) {
          if (setCurrentPage) setCurrentPage('inspections');
        }
      } else if (response.status === 504 || response.status === 502) {
        // Backend may have completed despite the gateway timeout. Pull latest record and show it here.
        try {
          const items = await api.loadInspections();
          if (items) {
            const startedAt = requestStartedAtMs;
            const candidates = items
              .filter(i => i && i.created_at)
              .map(i => ({ item: i, ts: Date.parse(i.created_at || 0) || 0 }))
              .filter(x => x.ts >= startedAt)
              .sort((a, b) => b.ts - a.ts);
            if (candidates.length > 0) {
              const latest = candidates[0].item;
              setInspectionResult({
                success: true,
                inspection_id: latest.id,
                analysis: latest.gemini_response || latest.analysis || {}
              });
              setSelectedImage(null);
              setLocation('');
              setNotes('');
              await refreshInspections();
              return; // Do not show the timeout alert since we recovered the result
            }
          }
        } catch (_) {
          // fall through to soft alert
        }
        
        if (showToast) showToast('⏰ Analysis timed out, but your inspection may have been saved. Check the Data tab.', 'warning', 6000);
      } else {
        const error = await response.json();
        console.error('Analysis failed:', error);
        if (showToast) showToast(`❌ ${error.detail || 'Analysis failed'}`, 'error');
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      if (showToast) showToast('❌ Analysis failed. Please try again.', 'error');
    } finally {
      setIsAnalyzing(false);
      setShowProcessingModal(false);
      setAnalyzeProgress(100);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  /**
   * Progress animation while analyzing
   */
  useEffect(() => {
    if (!showProcessingModal) return;
    let pct = 0;
    const timer = setInterval(() => {
      pct = Math.min(95, pct + Math.max(1, Math.floor(Math.random() * 6)));
      setAnalyzeProgress(pct);
    }, 500);
    return () => clearInterval(timer);
  }, [showProcessingModal]);

  /**
   * Fetch nearest affiliate when GPS exists
   */
  useEffect(() => {
    const fetchNearest = async () => {
      if (!gpsData || !gpsData.latitude || !gpsData.longitude) return;
      try {
        setLoadingNearest(true);
        const data = await api.fetchNearestAffiliate(gpsData.latitude, gpsData.longitude, 1);
        setNearestOffice((data.offices && data.offices[0]) || null);
      } catch (_) {
        // ignore
      } finally {
        setLoadingNearest(false);
      }
    };
    fetchNearest();
  }, [gpsData]);

  // ============================================================================
  // RETURN ALL STATE & FUNCTIONS
  // ============================================================================
  return {
    // Image state
    selectedImage,
    setSelectedImage,
    capturedImages,
    setCapturedImages,
    
    // Location & GPS state
    location,
    setLocation,
    businessName,
    setBusinessName,
    selectedLocations,
    setSelectedLocations,
    gpsData,
    setGpsData,
    isCapturingGPS,
    setIsCapturingGPS,
    gpsError,
    setGpsError,
    nearestOffice,
    setNearestOffice,
    loadingNearest,
    setLoadingNearest,
    
    // Business search state
    businessSuggestions,
    setBusinessSuggestions,
    isLoadingSuggestions,
    setIsLoadingSuggestions,
    showSuggestions,
    setShowSuggestions,
    showLocationPopup,
    setShowLocationPopup,
    locationSelectedFromPopup,
    setLocationSelectedFromPopup,
    
    // Submission flow state
    notes,
    setNotes,
    isSubmitting,
    setIsSubmitting,
    inspectionResult,
    setInspectionResult,
    isAnalyzing,
    setIsAnalyzing,
    showProcessingModal,
    setShowProcessingModal,
    analyzeProgress,
    setAnalyzeProgress,
    
    // Confirmation & success popups
    showConfirmationPopup,
    setShowConfirmationPopup,
    confirmationData,
    setConfirmationData,
    showSuccessPopup,
    setShowSuccessPopup,
    successPopupData,
    setSuccessPopupData,
    
    // Attachments & notes
    additionalNotes,
    setAdditionalNotes,
    additionalImages,
    setAdditionalImages,
    quickShotOtherNotes,
    setQuickShotOtherNotes,
    mainPageAttachments,
    setMainPageAttachments,
    
    // Gamification
    consecutiveScans,
    setConsecutiveScans,
    
    // Functions
    captureGPS,
    fetchBusinessSuggestions,
    handleFileUpload,
    handleMainPageFileUpload,
    handleQuickShotSubmit,
    analyzeImage
  };
};

export default useSubmission;

